import React, { useState } from 'react';
import { 
  startProjectStudentMigration, 
  validateProjectStudentMigration,
  migrateSpecificClassroom,
  ProjectStudentMigrationResponse,
  ProjectStudentValidationResponse
} from '../services/ProjectStudentMigration.service';

interface MigrationResult {
  totalStudents: number;
  migrated: number;
  skipped: number;
  errors: number;
  errorDetails?: string[];
}

interface ValidationResult {
  totalStudents: number;
  hasProjectRelations: number;
  missingRelations: number;
  missingStudents?: string[];
}

const ProjectStudentMigration: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [migrationResult, setMigrationResult] = useState<MigrationResult | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [classroomId, setClassroomId] = useState<string>('');

  const handleStartMigration = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setMigrationResult(null);

    try {
      const response: ProjectStudentMigrationResponse = await startProjectStudentMigration();
      if (response.status === 200) {
        setSuccess('Project-student migration completed successfully');
        setMigrationResult(response.data || null);
      } else {
        setError(response.message || 'Failed to start project-student migration');
      }
    } catch (err) {
      setError('Failed to start project-student migration');
      console.error('Project-student migration error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidateMigration = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setValidationResult(null);

    try {
      const response: ProjectStudentValidationResponse = await validateProjectStudentMigration();
      if (response.status === 200) {
        setSuccess('Migration validation completed successfully');
        setValidationResult(response.data || null);
      } else {
        setError(response.message || 'Failed to validate project-student migration');
      }
    } catch (err) {
      setError('Failed to validate project-student migration');
      console.error('Project-student validation error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClassroomMigration = async () => {
    if (!classroomId.trim()) {
      setError('Please enter a classroom ID');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);
    setMigrationResult(null);

    try {
      const response: ProjectStudentMigrationResponse = await migrateSpecificClassroom(classroomId.trim());
      if (response.status === 200) {
        setSuccess(`Classroom ${classroomId} migration completed successfully`);
        setMigrationResult(response.data || null);
      } else {
        setError(response.message || 'Failed to migrate classroom');
      }
    } catch (err) {
      setError('Failed to migrate classroom');
      console.error('Classroom migration error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Project-Student Migration</h2>
        <div className="flex gap-2">
          <button
            onClick={handleStartMigration}
            disabled={isLoading}
            className="px-4 py-2 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Running...' : 'Start Full Migration'}
          </button>
          <button
            onClick={handleValidateMigration}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Validating...' : 'Validate Migration'}
          </button>
        </div>
      </div>

      {/* Classroom-specific migration */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium text-gray-800 mb-3">Migrate Specific Classroom</h3>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label htmlFor="classroomId" className="block text-sm font-medium text-gray-700 mb-1">
              Classroom ID
            </label>
            <input
              type="text"
              id="classroomId"
              value={classroomId}
              onChange={(e) => setClassroomId(e.target.value)}
              placeholder="Enter classroom ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            />
          </div>
          <button
            onClick={handleClassroomMigration}
            disabled={isLoading || !classroomId.trim()}
            className="px-4 py-2 bg-yellow-600 text-white rounded-md font-medium hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Migrating...' : 'Migrate Classroom'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {/* Migration Results */}
      {migrationResult && (
        <div className="mb-6 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-800 mb-3">Migration Results</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-500">Total Students</div>
              <div className="text-lg font-semibold text-gray-900">
                {migrationResult.totalStudents}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-500">Migrated</div>
              <div className="text-lg font-semibold text-green-600">
                {migrationResult.migrated}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-500">Skipped</div>
              <div className="text-lg font-semibold text-yellow-600">
                {migrationResult.skipped}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-500">Errors</div>
              <div className="text-lg font-semibold text-red-600">
                {migrationResult.errors}
              </div>
            </div>
          </div>

          {/* Error Details */}
          {migrationResult.errorDetails && migrationResult.errorDetails.length > 0 && (
            <div className="bg-red-50 p-3 rounded-lg">
              <div className="text-sm font-medium text-red-700 mb-2">
                Error Details (showing first 5):
              </div>
              <div className="max-h-32 overflow-y-auto">
                {migrationResult.errorDetails.slice(0, 5).map((error, index) => (
                  <div key={index} className="text-sm text-red-600 mb-1">
                    • {error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Validation Results */}
      {validationResult && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-800 mb-3">Validation Results</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-500">Total Students</div>
              <div className="text-lg font-semibold text-gray-900">
                {validationResult.totalStudents}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-500">Has Project Relations</div>
              <div className="text-lg font-semibold text-green-600">
                {validationResult.hasProjectRelations}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-500">Missing Relations</div>
              <div className="text-lg font-semibold text-red-600">
                {validationResult.missingRelations}
              </div>
            </div>
          </div>

          {/* Missing Students */}
          {validationResult.missingStudents && validationResult.missingStudents.length > 0 && (
            <div className="bg-yellow-50 p-3 rounded-lg">
              <div className="text-sm font-medium text-yellow-700 mb-2">
                Missing Students (showing first 10):
              </div>
              <div className="max-h-32 overflow-y-auto">
                {validationResult.missingStudents.slice(0, 10).map((student, index) => (
                  <div key={index} className="text-sm text-yellow-600 mb-1">
                    • {student}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Success Summary */}
          {validationResult.missingRelations === 0 && (
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-sm font-medium text-green-700">
                ✅ All students have project relationships! Migration is complete.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="mt-6 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
        <div className="font-medium text-blue-800 mb-1">Migration Steps:</div>
        <div className="space-y-1">
          <div>1. <strong>Start Full Migration:</strong> Migrates all students from old User.classroomId structure to new ProjectStudent relationships</div>
          <div>2. <strong>Validate Migration:</strong> Checks if all students have proper project relationships</div>
          <div>3. <strong>Migrate Classroom:</strong> Migrates students from a specific classroom only</div>
        </div>
      </div>
    </div>
  );
};

export default ProjectStudentMigration;